import React, { useState, useEffect } from 'react';
import { X, Save, Calendar, DollarSign, Tag, FileText, Trash2, Plus, Check, AlertCircle, ChevronDown, ChevronRight, Sparkles, Receipt, Flag, CreditCard } from 'lucide-react';
import apiService from '../../services/api';
import IconPicker from '../common/IconPicker';
import AiCategorizeModal from './AiCategorizeModal';
import LinkModal from './LinkModal';
import ReceiptModal from './ReceiptModal';
import BaseModal from '../common/BaseModal';
import { useConfirmation } from '../../hooks/useConfirmation';

const TransactionDetailsModal = ({
  isOpen,
  onClose,
  transaction,
  categoryOptions,
  onSave,
  onDelete,
  formatCurrency,
  formatDate,
  onCategoryCreated, // Callback to refresh categories in parent
  onTransactionUpdated, // Callback when transaction is updated (e.g., receipt linked)
  onMarkStatementEnd // Callback to mark transaction as statement end
}) => {
  const [formData, setFormData] = useState({
    description: '',
    ai_category: '',
    category_icon: '',
    transaction_date: '',
    total_amount: 0,
    is_statement_payment: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isCreatingCategory, setIsCreatingCategory] = useState(false);
  const [newCategoryData, setNewCategoryData] = useState({
    name: '',
    label: '',
    icon: '💰',
    color: 'blue'
  });
  const [categoryCreationError, setCategoryCreationError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isIconPickerOpen, setIsIconPickerOpen] = useState(false);
  const [isCategoryExpanded, setIsCategoryExpanded] = useState(false);
  const [isAiModalOpen, setIsAiModalOpen] = useState(false);
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [isReceiptModalOpen, setIsReceiptModalOpen] = useState(false);
  const [fullReceipt, setFullReceipt] = useState(null);
  const [loadingReceipt, setLoadingReceipt] = useState(false);
  const { confirm, ConfirmationModal } = useConfirmation();

  useEffect(() => {
    if (transaction) {
      setFormData({
        description: transaction.description || '',
        ai_category: transaction.ai_category || 'other',
        category_icon: transaction.category_icon || '💰',
        transaction_date: transaction.transaction_date || '',
        total_amount: transaction.total_amount || 0,
        is_statement_payment: transaction.is_statement_payment || false
      });
      setIsLinkModalOpen(false); // Reset Link Receipt modal state when transaction changes
      setIsReceiptModalOpen(false); // Reset Receipt modal state when transaction changes
    }
  }, [transaction]);

  // Reset modal states when TransactionDetailsModal closes
  useEffect(() => {
    if (!isOpen) {
      setIsLinkModalOpen(false);
      setIsReceiptModalOpen(false);
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await onSave(transaction.id, formData);
      onClose();
    } catch (err) {
      console.error('Error saving transaction:', err);
      setError('Failed to save transaction. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (newCategory) => {
    const categoryOption = (categoryOptions || []).find(opt => opt.value === newCategory);
    setFormData({
      ...formData,
      ai_category: newCategory,
      category_icon: categoryOption?.icon || '💰'
    });
  };

  const handleCreateCategory = async () => {
    try {
      setCategoryCreationError(null);

      if (!newCategoryData.name || !newCategoryData.label) {
        setCategoryCreationError('Name and label are required');
        return;
      }

      // Create the category
      const createdCategory = await apiService.createCategory(newCategoryData);

      // Notify parent to refresh categories
      if (onCategoryCreated) {
        await onCategoryCreated();
      }

      // Automatically select the new category
      setFormData({
        ...formData,
        ai_category: createdCategory.name,
        category_icon: createdCategory.icon
      });

      // Reset form and close creation UI
      setNewCategoryData({
        name: '',
        label: '',
        icon: '💰',
        color: 'blue'
      });
      setIsCreatingCategory(false);

    } catch (err) {
      console.error('Error creating category:', err);
      setCategoryCreationError('Failed to create category. Please try again.');
    }
  };

  const handleCancelCategoryCreation = () => {
    setIsCreatingCategory(false);
    setNewCategoryData({
      name: '',
      label: '',
      icon: '💰',
      color: 'blue'
    });
    setCategoryCreationError(null);
  };

  const handleIconSelect = (icon) => {
    setNewCategoryData({ ...newCategoryData, icon });
  };

  const handleDeleteClick = async () => {
    const confirmed = await confirm(
      'Delete Transaction',
      'Are you sure you want to delete this transaction? This action cannot be undone.',
      'danger',
      'Delete',
      'Cancel'
    );

    if (!confirmed) return;

    try {
      await onDelete(transaction.id);
      onClose();
    } catch (err) {
      console.error('Error deleting transaction:', err);
      setError('Failed to delete transaction. Please try again.');
    }
  };

  const handleViewReceipt = async () => {
    if (!transaction?.receipt?.id) return;

    try {
      setLoadingReceipt(true);
      const receiptData = await apiService.getReceipt(transaction.receipt.id);
      setFullReceipt(receiptData);
      setIsReceiptModalOpen(true);
    } catch (err) {
      console.error('Error loading receipt:', err);
      setError('Failed to load receipt details');
    } finally {
      setLoadingReceipt(false);
    }
  };

  const handleAiCategorize = () => {
    setIsAiModalOpen(true);
    setIsCategoryExpanded(true); // Expand category section to show the change
  };

  const handleAiCategoryConfirm = async (category, categoryIcon, categoryLabel) => {
    // Update the form data with AI-selected category
    setFormData({
      ...formData,
      ai_category: category,
      category_icon: categoryIcon
    });
    setIsAiModalOpen(false);
  };

  const filteredCategories = (categoryOptions || []).filter(option =>
    option.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
    option.value.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!transaction) return null;

  return (
    <>
    <BaseModal isOpen={isOpen} onClose={onClose} size="lg" showCloseButton={false}>
      {/* Custom Header with X button */}
      <div className="px-4 md:px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <h3 className="text-base md:text-lg font-medium text-gray-900 dark:text-white">Transaction Details</h3>
        <button
          onClick={onClose}
          className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          disabled={loading}
          aria-label="Close modal"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Content */}
      <div className="p-4 md:p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
            {/* Transaction Info Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              {/* Date */}
              <div>
                <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="h-4 w-4 mr-2" />
                  Date
                </label>
                <input
                  type="date"
                  value={formData.transaction_date}
                  onChange={(e) => setFormData({ ...formData, transaction_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Amount */}
              <div>
                <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                  <DollarSign className="h-4 w-4 mr-2" />
                  Amount
                </label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.01"
                    value={formData.total_amount}
                    onChange={(e) => setFormData({ ...formData, total_amount: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <div className="absolute right-3 top-2 text-sm text-gray-500">
                    {formatCurrency(formData.total_amount)}
                  </div>
                </div>
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                <FileText className="h-4 w-4 mr-2" />
                Description
              </label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Transaction description"
              />
            </div>

            {/* Statement Payment Toggle */}
            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
              <div className="flex items-center">
                <CreditCard className="h-4 w-4 mr-2 text-purple-500 dark:text-purple-400" />
                <div>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Statement Payment</span>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Mark if this pays off a previous statement (excluded from current statement balance)
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, is_statement_payment: !formData.is_statement_payment })}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                  formData.is_statement_payment ? 'bg-purple-600' : 'bg-gray-200 dark:bg-gray-600'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    formData.is_statement_payment ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            {/* Category */}
            <div>
              {/* Category Header with Toggle */}
              <button
                type="button"
                onClick={() => setIsCategoryExpanded(!isCategoryExpanded)}
                className="flex items-center justify-between w-full text-sm font-medium text-gray-700 mb-2 hover:text-gray-900 transition-colors"
              >
                <div className="flex items-center">
                  <Tag className="h-4 w-4 mr-2" />
                  Category
                </div>
                <div className="flex items-center">
                  {/* Current Category Display */}
                  <div className="inline-flex items-center px-2 py-1 rounded-md bg-gray-50 border border-gray-200 mr-2">
                    <span className="text-sm mr-1">{formData.category_icon}</span>
                    <span className="text-xs font-medium text-gray-600">
                      {(categoryOptions || []).find(opt => opt.value === formData.ai_category)?.label || formData.ai_category}
                    </span>
                  </div>
                  {isCategoryExpanded ? (
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  )}
                </div>
              </button>

              {/* Collapsible Category Content */}
              {isCategoryExpanded && (
                <div className="space-y-3">
                  {/* Search and Create Controls */}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Search categories..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      type="button"
                      onClick={handleAiCategorize}
                      className="px-3 py-2 text-sm font-medium text-purple-600 bg-purple-50 border border-purple-200 rounded-md hover:bg-purple-100 flex items-center"
                    >
                      <Sparkles className="h-4 w-4 mr-1" />
                      AI
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsCreatingCategory(!isCreatingCategory)}
                      className="px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 flex items-center"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      New
                    </button>
                  </div>

              {/* Create New Category Form */}
              {isCreatingCategory && (
                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="text-sm font-medium text-blue-900 mb-3">Create New Category</h4>

                  {categoryCreationError && (
                    <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-600 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-2" />
                      {categoryCreationError}
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Name (key)</label>
                      <input
                        type="text"
                        value={newCategoryData.name}
                        onChange={(e) => setNewCategoryData({ ...newCategoryData, name: e.target.value })}
                        placeholder="e.g., gas_station"
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Display Label</label>
                      <input
                        type="text"
                        value={newCategoryData.label}
                        onChange={(e) => setNewCategoryData({ ...newCategoryData, label: e.target.value })}
                        placeholder="e.g., Gas Station"
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Icon</label>
                      <button
                        type="button"
                        onClick={() => setIsIconPickerOpen(true)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-center hover:bg-gray-50 flex items-center justify-center"
                      >
                        <span className="text-lg mr-2">{newCategoryData.icon}</span>
                        <span className="text-gray-500">Choose</span>
                      </button>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Color</label>
                      <select
                        value={newCategoryData.color}
                        onChange={(e) => setNewCategoryData({ ...newCategoryData, color: e.target.value })}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                      >
                        <option value="blue">Blue</option>
                        <option value="green">Green</option>
                        <option value="red">Red</option>
                        <option value="yellow">Yellow</option>
                        <option value="purple">Purple</option>
                        <option value="orange">Orange</option>
                        <option value="teal">Teal</option>
                        <option value="gray">Gray</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={handleCancelCategoryCreation}
                      className="px-3 py-1 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleCreateCategory}
                      className="px-3 py-1 text-sm text-white bg-blue-600 rounded hover:bg-blue-700 flex items-center"
                    >
                      <Check className="h-4 w-4 mr-1" />
                      Create
                    </button>
                  </div>
                </div>
              )}

              {/* Category Selector */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-48 overflow-y-auto border border-gray-200 rounded-md p-3">
                {filteredCategories.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleCategoryChange(option.value)}
                    className={`flex items-center px-3 py-2 text-sm rounded-md border-2 transition-colors ${
                      formData.ai_category === option.value
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <span className="mr-2">{option.icon}</span>
                    <span className="truncate">{option.label}</span>
                  </button>
                ))}

                {filteredCategories.length === 0 && searchQuery && (
                  <div className="col-span-full text-center py-4 text-gray-500 text-sm">
                    No categories found for "{searchQuery}"
                    <br />
                    <button
                      type="button"
                      onClick={() => {
                        setNewCategoryData({
                          ...newCategoryData,
                          name: searchQuery.toLowerCase().replace(/\s+/g, '_'),
                          label: searchQuery
                        });
                        setIsCreatingCategory(true);
                      }}
                      className="mt-2 text-blue-600 hover:text-blue-800 font-medium"
                    >
                      Create "{searchQuery}" category
                    </button>
                  </div>
                )}
              </div>
                </div>
              )}
            </div>

            {/* Transaction Metadata */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Transaction Information</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Transaction ID:</span>
                  <span className="ml-2 font-mono text-gray-900">#{transaction.id}</span>
                </div>
                <div>
                  <span className="text-gray-500">Type:</span>
                  <span className="ml-2 text-gray-900">{transaction.transaction_type || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-gray-500">Reference:</span>
                  <span className="ml-2 font-mono text-gray-900">{transaction.reference_id || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-gray-500">Balance After:</span>
                  <span className="ml-2 text-gray-900">{formatCurrency(transaction.running_balance || 0)}</span>
                </div>
              </div>
            </div>
          </form>
        </div>

      {/* Footer */}
      <div className="px-4 md:px-6 py-3 md:py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-750">
        {/* Desktop layout */}
        <div className="hidden md:flex justify-between">
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={handleDeleteClick}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-red-700 dark:text-red-400 bg-white dark:bg-gray-700 border border-red-300 dark:border-red-600 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 transition-colors"
              disabled={loading}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Transaction
            </button>
            {/* Show Link Receipt button if transaction doesn't have a receipt, otherwise show View Receipt */}
            {transaction?.receipt ? (
              <button
                type="button"
                onClick={handleViewReceipt}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-green-700 dark:text-green-400 bg-white dark:bg-gray-700 border border-green-300 dark:border-green-600 rounded-md hover:bg-green-50 dark:hover:bg-green-900/20 disabled:opacity-50 transition-colors"
                disabled={loading || loadingReceipt}
              >
                {loadingReceipt ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600 dark:border-green-400 mr-2"></div>
                    Loading...
                  </>
                ) : (
                  <>
                    <Receipt className="h-4 w-4 mr-2" />
                    View Receipt
                  </>
                )}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setIsLinkModalOpen(true)}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-pink-700 dark:text-pink-400 bg-white dark:bg-gray-700 border border-pink-300 dark:border-pink-600 rounded-md hover:bg-pink-50 dark:hover:bg-pink-900/20 disabled:opacity-50 transition-colors"
                disabled={loading}
              >
                <Receipt className="h-4 w-4 mr-2" />
                Link Receipt
              </button>
            )}
            <button
              type="button"
              onClick={() => onMarkStatementEnd && onMarkStatementEnd(transaction)}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-700 dark:text-blue-400 bg-white dark:bg-gray-700 border border-blue-300 dark:border-blue-600 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 disabled:opacity-50 transition-colors"
              disabled={loading || !onMarkStatementEnd}
            >
              <Flag className="h-4 w-4 mr-2" />
              Mark Statement End
            </button>
          </div>

          <div className="flex space-x-3">
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </>
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </div>

        {/* Mobile layout */}
        <div className="md:hidden space-y-2">
          {/* Action buttons - full width on mobile */}
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="w-full inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                <span>Save Changes</span>
              </>
            )}
          </button>

          <div className="grid grid-cols-2 gap-2">
            {/* Show Link Receipt button if transaction doesn't have a receipt, otherwise show View Receipt */}
            {transaction?.receipt ? (
              <button
                type="button"
                onClick={handleViewReceipt}
                className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-green-700 dark:text-green-400 bg-white dark:bg-gray-700 border border-green-300 dark:border-green-600 rounded-md hover:bg-green-50 dark:hover:bg-green-900/20 disabled:opacity-50 transition-colors"
                disabled={loading || loadingReceipt}
              >
                {loadingReceipt ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600 dark:border-green-400 mr-1"></div>
                    <span className="text-xs">Loading...</span>
                  </>
                ) : (
                  <>
                    <Receipt className="h-4 w-4 mr-1" />
                    <span className="text-xs">View Receipt</span>
                  </>
                )}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setIsLinkModalOpen(true)}
                className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-pink-700 dark:text-pink-400 bg-white dark:bg-gray-700 border border-pink-300 dark:border-pink-600 rounded-md hover:bg-pink-50 dark:hover:bg-pink-900/20 disabled:opacity-50 transition-colors"
                disabled={loading}
              >
                <Receipt className="h-4 w-4 mr-1" />
                <span className="text-xs">Link Receipt</span>
              </button>
            )}

            <button
              type="button"
              onClick={handleDeleteClick}
              className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-red-700 dark:text-red-400 bg-white dark:bg-gray-700 border border-red-300 dark:border-red-600 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 transition-colors"
              disabled={loading}
            >
              <Trash2 className="h-4 w-4 mr-1" />
              <span className="text-xs">Delete</span>
            </button>
          </div>

          <button
            type="button"
            onClick={() => onMarkStatementEnd && onMarkStatementEnd(transaction)}
            className="w-full inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-blue-700 dark:text-blue-400 bg-white dark:bg-gray-700 border border-blue-300 dark:border-blue-600 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 disabled:opacity-50 transition-colors"
            disabled={loading || !onMarkStatementEnd}
          >
            <Flag className="h-4 w-4 mr-2" />
            <span>Mark Statement End</span>
          </button>

          <button
            type="button"
            onClick={onClose}
            className="w-full px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
            disabled={loading}
          >
            Cancel
          </button>
        </div>
      </div>
    </BaseModal>

      {/* Icon Picker Modal */}
      <IconPicker
        isOpen={isIconPickerOpen}
        onClose={() => setIsIconPickerOpen(false)}
        onSelect={handleIconSelect}
        selectedIcon={newCategoryData.icon}
      />

      {/* AI Categorize Modal */}
      <AiCategorizeModal
        isOpen={isAiModalOpen}
        onClose={() => setIsAiModalOpen(false)}
        onConfirm={handleAiCategoryConfirm}
        transaction={transaction}
      />

      {/* Link Receipt Modal */}
      <LinkModal
        isOpen={isLinkModalOpen}
        onClose={() => setIsLinkModalOpen(false)}
        transaction={transaction}
        onLinked={async () => {
          setIsLinkModalOpen(false);
          // Notify parent to refresh transaction list
          onTransactionUpdated?.();
        }}
      />

      {/* Receipt Modal - for viewing linked receipt */}
      {fullReceipt && (
        <ReceiptModal
          isOpen={isReceiptModalOpen}
          onClose={() => {
            setIsReceiptModalOpen(false);
            setFullReceipt(null); // Clear the receipt when closing
          }}
          receipt={fullReceipt}
          accounts={[]}
          currentAccount={null}
          onReceiptUpdated={() => {
            // Could refresh transaction data here if needed
          }}
        />
      )}

      <ConfirmationModal />
    </>
  );
};

export default TransactionDetailsModal;